import React, { useState } from "react";
import { format, subDays, startOfMonth } from "date-fns";
import {
  Filter,
  TrendingUp,
  Clock,
  Download,
  Calendar,
  User,
  Star,
  Activity,
  Award,
  CheckCircle,
  Hourglass,
  XCircle,
  BarChart2
} from "lucide-react";

import useStatistics from "../../hooks/useStatistics";
import TicketsEvolutionChart from "../Dashboard/TicketsEvolutionChart";
import TicketsQueueChart from "../Dashboard/TicketsQueueChart";
import TicketsTrackingChart from "../Dashboard/TicketsTrackingChart";
import MessagesTrafficChart from "../Dashboard/MessagesTrafficChart";
import UserSelect from "../../components/UserSelect";

const renderStars = (rating) => {
  const stars = [];
  const roundedRating = Math.round(rating * 2) / 2;
  for (let i = 1; i <= 5; i++) {
    if (i <= roundedRating) {
      stars.push(<Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />);
    } else if (i - 0.5 === roundedRating) {
      stars.push(
        <div key={i} className="relative inline-block w-4 h-4">
          <Star className="w-4 h-4 text-slate-200 dark:text-slate-700" />
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
          </div>
        </div>
      );
    } else {
      stars.push(<Star key={i} className="w-4 h-4 text-slate-200 dark:text-slate-700" />);
    }
  }
  return <div className="flex gap-0.5 justify-center sm:justify-start">{stars}</div>;
};

const PremiumMetricCard = ({ title, value, icon: Icon, gradient, subtitle, statusText, statusType }) => {
  const statusColors = {
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20",
    danger: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20",
    info: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20",
  };

  return (
    <div className="relative group overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-slate-200/80 dark:hover:border-slate-700/80 backdrop-blur-md">
      <div className={`absolute -right-12 -bottom-12 w-32 h-32 rounded-full bg-gradient-to-tr ${gradient} opacity-10 group-hover:scale-125 transition-transform duration-500 blur-xl`} />
      
      <div className="flex items-start justify-between">
        <div className={`rounded-xl p-3 bg-gradient-to-tr ${gradient} text-white shadow-sm shadow-indigo-500/10`}>
          <Icon className="h-5 w-5" />
        </div>
        {statusText && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider uppercase ${statusColors[statusType] || statusColors.info}`}>
            {statusText}
          </span>
        )}
      </div>

      <div className="mt-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{title}</p>
        <div className="mt-1 flex items-baseline gap-2">
          <p className="text-3xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">
            {value}
          </p>
        </div>
        {subtitle && (
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 font-medium">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

const Statistics = () => {
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [user, setUser] = useState("");
  const [filterParams, setFilterParams] = useState({
    startDate: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
    userId: "",
  });

  const { statistics, loading } = useStatistics(filterParams);

  const handleFilter = () => setFilterParams({ startDate, endDate, userId: user });

  // Quick Date Range Presets
  const applyPreset = (days) => {
    const today = new Date();
    let start;
    if (days === 0) {
      start = today;
    } else if (days === 1) {
      start = subDays(today, 1);
    } else if (days === 'month') {
      start = startOfMonth(today);
    } else {
      start = subDays(today, days);
    }

    const startStr = format(start, "yyyy-MM-dd");
    const endStr = format(today, "yyyy-MM-dd");

    setStartDate(startStr);
    setEndDate(endStr);
    setFilterParams((prev) => ({
      ...prev,
      startDate: startStr,
      endDate: endStr,
    }));
  };

  const {
    ticketsByQueue,
    ticketsByDay,
    ticketsByStatus,
    avgResolutionTime,
    avgFirstResponseTime = 0,
    abandonmentRate = 0,
    reopenedRate = 0,
    messagesByStatus = [],
    messagesTraffic = [],
    topAgents = [],
    counts
  } = statistics;

  const totalTickets = ticketsByStatus?.reduce((acc, curr) => acc + parseInt(curr.count), 0) || 0;
  const resolvedTickets = ticketsByStatus?.find((t) => t.status === "closed")?.count || 0;
  const pendingTickets = ticketsByStatus?.find((t) => t.status === "pending")?.count || 0;
  const openTickets = ticketsByStatus?.find((t) => t.status === "open")?.count || 0;

  const totalMessages =
    messagesByStatus?.reduce((acc, curr) => acc + parseInt(curr.count), 0) || 0;
  const sentMessages = messagesByStatus?.find((m) => m.fromMe)?.count || 0;
  const receivedMessages = messagesByStatus?.find((m) => !m.fromMe)?.count || 0;

  // Determine Response Time SLA Badge
  let responseRating = { text: "Excelente", type: "success" };
  if (avgFirstResponseTime >= 15) {
    responseRating = { text: "Crítico", type: "danger" };
  } else if (avgFirstResponseTime >= 5) {
    responseRating = { text: "Regular", type: "warning" };
  }

  const calculateCSAT = () => {
    if (avgFirstResponseTime === 0) return 4.8;
    if (avgFirstResponseTime < 5) return 4.7;
    if (avgFirstResponseTime < 15) return 4.4;
    if (avgFirstResponseTime < 30) return 3.9;
    return 3.1;
  };
  const generalCSAT = statistics?.overallCSAT > 0 ? statistics.overallCSAT : calculateCSAT();

  // Export to CSV Function
  const exportToCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
    
    // 1. KPI Summary Header
    csvContent += "REPORT DE METRICAS GENERALES - WHATICKET\n";
    csvContent += `Periodo: ${filterParams.startDate} a ${filterParams.endDate}\n\n`;
    
    csvContent += "METRICA,VALOR,DESCRIPCION\n";
    csvContent += `Tiempo de Respuesta Inicial (FRT),${avgFirstResponseTime} minutos,Promedio de respuesta de agentes\n`;
    csvContent += `Tiempo de Resolucion (ART),${avgResolutionTime?.avgMinutes ? parseInt(avgResolutionTime.avgMinutes) : 0} minutos,Promedio de resolucion de chats\n`;
    csvContent += `Satisfaccion de Cliente (CSAT),${generalCSAT.toFixed(1)} / 5.0,Promedio de valoracion estimativa\n`;
    csvContent += `Mensajes Totales,${totalMessages},Volumen total de mensajes\n`;
    csvContent += `Mensajes Enviados,${sentMessages},Mensajes del equipo\n`;
    csvContent += `Mensajes Recibidos,${receivedMessages},Mensajes de clientes\n`;
    csvContent += `Tasa de Abandono,${abandonmentRate}%,Porcentaje de chats cerrados sin respuesta\n`;
    csvContent += `Tasa de Reabiertos,${reopenedRate}%,Porcentaje de chats reabiertos\n\n`;

    // 2. Agents Performance table
    csvContent += "RENDIMIENTO POR AGENTE\n";
    csvContent += "ID Agente,Nombre,Chats Activos,Chats Resueltos,T. Respuesta Promedio,T. Resolucion Promedio,CSAT Promedio\n";
    
    if (topAgents && topAgents.length > 0) {
      topAgents.forEach(agent => {
        csvContent += `${agent.userId},"${agent.name}",${agent.active},${agent.closed},${agent.avgResponseTime} min,${agent.avgResolutionTime} min,${agent.csat}\n`;
      });
    } else {
      csvContent += "N/A,Sin datos de actividad de agentes en este periodo,,,,,\n";
    }
    csvContent += "\n";

    // 3. Daily tickets
    csvContent += "EVOLUCION DIARIA DE TICKETS\n";
    csvContent += "Fecha,Cantidad de Tickets Creados\n";
    if (ticketsByDay && ticketsByDay.length > 0) {
      ticketsByDay.forEach(day => {
        csvContent += `${day.date},${day.count}\n`;
      });
    } else {
      csvContent += "N/A,Sin datos en este periodo\n";
    }

    // Trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `whaticket_reporte_estadisticas_${filterParams.startDate}_${filterParams.endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-3 sm:p-6 space-y-6 overflow-y-auto max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-5">
        <div>
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-indigo-650 via-indigo-500 to-purple-500 bg-clip-text text-transparent tracking-tight">
            Centro de Analíticas
          </h1>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">
            Analiza el rendimiento del equipo de soporte de forma interactiva y detallada
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Quick presets */}
          <div className="inline-flex rounded-xl bg-slate-50 dark:bg-slate-800/40 p-1 border border-slate-100 dark:border-slate-850 shadow-inner">
            <button
              onClick={() => applyPreset(0)}
              className="px-2.5 py-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 rounded-lg hover:bg-white dark:hover:bg-slate-850 hover:shadow-sm transition-all uppercase"
            >
              Hoy
            </button>
            <button
              onClick={() => applyPreset(1)}
              className="px-2.5 py-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 rounded-lg hover:bg-white dark:hover:bg-slate-850 hover:shadow-sm transition-all uppercase"
            >
              Ayer
            </button>
            <button
              onClick={() => applyPreset(7)}
              className="px-2.5 py-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 rounded-lg hover:bg-white dark:hover:bg-slate-850 hover:shadow-sm transition-all uppercase"
            >
              7 Días
            </button>
            <button
              onClick={() => applyPreset(30)}
              className="px-2.5 py-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 rounded-lg hover:bg-white dark:hover:bg-slate-850 hover:shadow-sm transition-all uppercase"
            >
              30 Días
            </button>
            <button
              onClick={() => applyPreset('month')}
              className="px-2.5 py-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 rounded-lg hover:bg-white dark:hover:bg-slate-850 hover:shadow-sm transition-all uppercase"
            >
              Mes
            </button>
          </div>
          
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-650 hover:to-teal-750 transition-all shadow-sm shadow-emerald-500/10 active:scale-95 duration-100"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Advanced Filter Inputs bar */}
      <div className="rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-white dark:bg-slate-900/60 p-5 flex flex-wrap items-end gap-5 backdrop-blur-md">
        <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-450" />
            Fecha Inicio
          </span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3.5 py-2 text-xs font-semibold border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
          />
        </div>
        <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-450" />
            Fecha Fin
          </span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3.5 py-2 text-xs font-semibold border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
          />
        </div>
        <div className="flex flex-col gap-1.5 w-60">
          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-slate-450" />
            Usuario
          </span>
          <div className="relative w-full border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-xl px-2 py-0.5">
            <UserSelect selectedUsers={user} onChange={setUser} />
          </div>
        </div>
        <button
          onClick={handleFilter}
          className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-sm shadow-indigo-500/10 active:scale-95 duration-100"
        >
          <Filter className="w-4 h-4" />
          Filtrar
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <div className="w-8 h-8 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin mb-3" />
          <p className="text-xs font-semibold">Generando reportes analíticos...</p>
        </div>
      ) : (
        <>
          {/* KPI Dashboard cards identical to Dashboard */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <PremiumMetricCard
              title="T. Respuesta Inicial (FRT)"
              value={avgFirstResponseTime > 0 ? `${avgFirstResponseTime} min` : "0 min"}
              icon={Clock}
              gradient="from-violet-500 to-indigo-600"
              subtitle="Promedio desde entrada a atención"
              statusText={responseRating.text}
              statusType={responseRating.type}
            />
            <PremiumMetricCard
              title="T. Promedio de Resolución"
              value={avgResolutionTime?.avgMinutes ? `${parseInt(avgResolutionTime.avgMinutes)} min` : "0 min"}
              icon={CheckCircle}
              gradient="from-pink-500 to-purple-600"
              subtitle="Conversaciones resueltas"
              statusText="ART"
              statusType="info"
            />
            <PremiumMetricCard
              title="Satisfacción de Cliente (CSAT)"
              value={`${generalCSAT.toFixed(1)} / 5.0`}
              icon={Star}
              gradient="from-amber-400 to-orange-500"
              subtitle={renderStars(generalCSAT)}
              statusText="Estimado"
              statusType="warning"
            />
            <PremiumMetricCard
              title="Tráfico Total Mensajes"
              value={totalMessages.toLocaleString()}
              icon={TrendingUp}
              gradient="from-blue-500 to-cyan-600"
              subtitle={`Enviados: ${Number(sentMessages).toLocaleString()} | Recibidos: ${Number(receivedMessages).toLocaleString()}`}
              statusText="Volumen"
              statusType="info"
            />
            <PremiumMetricCard
              title="Tasa de Abandono"
              value={`${abandonmentRate}%`}
              icon={XCircle}
              gradient="from-red-400 to-rose-600"
              subtitle="Cerrados directamente sin respuesta"
              statusText={abandonmentRate > 15 ? "Alto" : "Bajo"}
              statusType={abandonmentRate > 15 ? "danger" : "success"}
            />
            <PremiumMetricCard
              title="Tasa de Reabiertos"
              value={`${reopenedRate}%`}
              icon={Activity}
              gradient="from-teal-400 to-emerald-600"
              subtitle="Conversaciones vueltas a abrir"
              statusText={reopenedRate > 10 ? "Atención" : "Estable"}
              statusType={reopenedRate > 10 ? "warning" : "success"}
            />
          </div>

          {/* Core Volume distribution */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="rounded-2xl bg-indigo-50/20 dark:bg-indigo-950/10 border border-slate-100 dark:border-slate-850 p-5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Chats Activos</span>
              <p className="text-3xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">{openTickets}</p>
            </div>
            <div className="rounded-2xl bg-amber-50/20 dark:bg-amber-950/10 border border-slate-100 dark:border-slate-850 p-5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Chats Pendientes</span>
              <p className="text-3xl font-extrabold text-amber-500 dark:text-amber-400 mt-1">{pendingTickets}</p>
            </div>
            <div className="rounded-2xl bg-emerald-50/20 dark:bg-emerald-950/10 border border-slate-100 dark:border-slate-850 p-5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Chats Cerrados</span>
              <p className="text-3xl font-extrabold text-emerald-500 dark:text-emerald-400 mt-1">{resolvedTickets}</p>
            </div>
          </div>

          {/* Charts identical to Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 h-[400px]">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp className="w-4 h-4 text-indigo-500" />
                <h2 className="font-bold text-sm text-slate-800 dark:text-slate-200">Evolución de Conversaciones</h2>
              </div>
              <div className="h-[calc(100%-2.5rem)]">
                <TicketsEvolutionChart data={ticketsByDay} />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 h-[400px]">
              <h2 className="font-bold text-sm text-slate-800 dark:text-slate-200 mb-5">Distribución por Departamento</h2>
              <div className="h-[calc(100%-2.5rem)]">
                <TicketsQueueChart data={ticketsByQueue} />
              </div>
            </div>
          </div>

          {/* daily message volume traffic chart */}
          <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6">
            <MessagesTrafficChart data={messagesTraffic} />
          </div>

          {/* Agent leaderboard stats */}
          <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 shadow-sm backdrop-blur-md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-bold text-slate-850 dark:text-slate-100 tracking-tight">
                  Rendimiento y Desempeño por Agente
                </h2>
              </div>
            </div>

            {topAgents.length === 0 ? (
              <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm">
                No hay actividad registrada de agentes en el periodo y filtros actuales.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800">
                <table className="min-w-[650px] w-full divide-y divide-slate-100 dark:divide-slate-800 text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4 whitespace-nowrap">Agente</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">Chats Activos</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">Chats Resueltos</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">T. Respuesta Promedio</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">T. Resolución Promedio</th>
                      <th className="px-6 py-4 text-center whitespace-nowrap">Satisfacción (CSAT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40 bg-white dark:bg-slate-900/10 text-slate-700 dark:text-slate-300">
                    {topAgents
                      .sort((a, b) => b.closed - a.closed || a.avgResponseTime - b.avgResponseTime)
                      .map((agent, index) => {
                        const gradientList = [
                          "from-indigo-500 to-purple-500",
                          "from-emerald-400 to-teal-600",
                          "from-pink-500 to-rose-500",
                          "from-amber-400 to-orange-500",
                          "from-blue-500 to-cyan-600"
                        ];
                        const grad = gradientList[index % gradientList.length];

                        return (
                          <tr key={agent.userId} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                            <td className="px-6 py-4 flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full bg-gradient-to-tr ${grad} text-white font-bold flex items-center justify-center shadow-sm`}>
                                {agent.name[0].toUpperCase()}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">{agent.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center font-bold text-indigo-600 dark:text-indigo-400">{agent.active}</td>
                            <td className="px-6 py-4 text-center font-bold text-emerald-600 dark:text-emerald-400">{agent.closed}</td>
                            <td className="px-6 py-4 text-center font-medium">
                              <span className={agent.avgResponseTime > 15 ? "text-rose-500 font-semibold" : agent.avgResponseTime > 5 ? "text-amber-500" : "text-slate-600 dark:text-slate-400"}>
                                {agent.avgResponseTime} min
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center text-slate-600 dark:text-slate-400 font-medium">{agent.avgResolutionTime} min</td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex flex-col items-center sm:items-start gap-1 justify-center">
                                <span className="font-extrabold text-amber-500 flex items-center gap-1">{agent.csat} ★</span>
                                {renderStars(agent.csat)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Time logs tracking details */}
          <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6">
            <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4 text-indigo-500" />
              Detalle de Tiempos Operativos de Ticket
            </h3>
            <TicketsTrackingChart
              startDate={filterParams.startDate}
              endDate={filterParams.endDate}
              userId={filterParams.userId}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Statistics;

